import { Icon } from "@single-js/common/components/icon/icon";
import { HYPHEN } from "@single-js/common/util/symbols";
import classnames from "classnames";
import { isPartitionImpacted, } from "data/models";
import * as React from "react";
import { logError } from "util/logging";
import "./cluster-metadata.scss";
const PARTITION_INSTANCE_STATUS_ICON = {
    online: "check-circle",
    replicating: "clone",
    provisioning: "cog",
    unrecoverable: "exclamation-circle",
    recovering: "sync-alt",
    transition: "exchange-alt",
    pending: "clock",
    offline: "exclamation-triangle",
    unknown: "question-circle",
};
const getPartitionInstanceStatusIconClass = (status) => {
    switch (status) {
        case "online":
            return "success";
        case "recovering":
        case "offline":
        case "unknown":
            return "warn";
        case "unrecoverable":
        case "offline":
            return "error";
    }
};
export const getPartitionInstanceStatusIcon = (status) => {
    const iconColor = getPartitionInstanceStatusIconClass(status);
    let styleProps;
    if (iconColor) {
        styleProps = {
            [iconColor]: true,
        };
    }
    let icon = PARTITION_INSTANCE_STATUS_ICON[status];
    if (icon === undefined) {
        icon = "question-circle";
        logError(new Error(`Partition instance status ${status} does not have an associated icon.`));
    }
    return React.createElement(Icon, { rightMargin: true, iconType: "regular", icon: icon, ...styleProps });
};
const displayPartitionInstanceState = (state) => {
    switch (state) {
        case "online":
            return "Online";
        case "replicating":
            return "Replicating";
        case "provisioning":
            return "Provisioning";
        case "unrecoverable":
            return "Unrecoverable";
        case "recovering":
            return "Recovering";
        case "transition":
            return "Transition";
        case "pending":
            return "Pending";
        case "unknown":
            return "Unknown";
        case "offline":
            return "Offline";
        default:
            return state;
    }
};
export const getPartitionInstanceStatusClass = (status) => {
    switch (status) {
        case "unrecoverable":
        case "offline":
            return "error";
    }
};
export const formatPartitionInstanceState = (status) => {
    const icon = getPartitionInstanceStatusIcon(status);
    const text = displayPartitionInstanceState(status);
    const classes = classnames("status-cell", getPartitionInstanceStatusClass(status));
    return (React.createElement("div", { className: classes },
        icon,
        " ",
        text));
};
export const formatPartitionInstanceRole = (role) => {
    switch (role) {
        case "master":
            return "Master";
        case "slave":
            return "Slave";
        case "detached master":
            return "Detached Master";
        case "detached slave":
            return "Detached Slave";
        default:
            return role;
    }
};
const PARTITION_STATUS_ICON = {
    online: "check-circle",
    degraded_recovering: "sync-alt",
    offline_recovering: "sync-alt",
    degraded_unrecoverable: "exclamation-circle",
    offline: "exclamation-triangle",
};
const _getPartitionStatusIcon = (status, impacted) => {
    if (status === "online" && impacted) {
        return "exclamation-circle";
    }
    else {
        return PARTITION_STATUS_ICON[status];
    }
};
const getPartitionStatusIconClass = (status, impacted) => {
    if (status === "online" && !impacted) {
        return "success";
    }
    else if ((status === "online" && impacted) ||
        status === "degraded_unrecoverable" ||
        status === "degraded_recovering") {
        return "warn";
    }
    else if (status === "offline_recovering" || status === "offline") {
        return "error";
    }
};
export const getPartitionStatusIcon = (status, impacted) => {
    const icon = _getPartitionStatusIcon(status, impacted);
    const iconState = getPartitionStatusIconClass(status, impacted);
    if (icon) {
        return (React.createElement(Icon, { rightMargin: true, iconType: "regular", icon: icon, state: iconState }));
    }
};
// This function can also be used to format database statuses.
export const displayPartitionStatus = (status, impacted) => {
    let text;
    switch (status) {
        case "online":
            text = "Online";
            break;
        case "degraded_recovering":
            text = "Degraded Recovering";
            break;
        case "degraded_unrecoverable":
            text = "Degraded Unrecoverable";
            break;
        case "offline_recovering":
            text = "Offline Recovering";
            break;
        case "offline":
            text = "Offline";
            break;
        default:
            text = "Unknown";
    }
    if (impacted) {
        text = `${text} ${HYPHEN} Impacted`;
    }
    return text;
};
export const getPartitionStatusClass = (status) => {
    switch (status) {
        case "offline_recovering":
        case "offline":
            return "error";
    }
};
export function formatPartitionStatus(partition) {
    return _formatPartitionStatus(partition.status, isPartitionImpacted(partition));
}
export const _formatPartitionStatus = (status, impacted) => {
    const text = displayPartitionStatus(status, impacted);
    const icon = getPartitionStatusIcon(status, impacted);
    const classes = classnames("status-cell", getPartitionStatusClass(status));
    return (React.createElement("div", { className: classes },
        icon,
        " ",
        text));
};
export const formatPartitionSyncState = (partition) => {
    if (partition.syncState === "async") {
        return "Asynchronous";
    }
    else if (partition.syncState === "sync") {
        return "Synchronous";
    }
    else if (typeof partition.syncState === "string") {
        return partition.syncState;
    }
};
